import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FaChevronDown, FaChevronRight, FaCheck } from "react-icons/fa6";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";

import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { getOrganizationList, getLocationList, switchAccountLocation } from "../services/admin/organization";
import Spinner from "./spinner";
import styles from "../styles/AccountLocationSwitcher.module.css";

const AccountLocationSwitcher = ({ showAccountSwitcher, showLocationSwitcher }) => {
  const queryClient = useQueryClient();
  const [locationsCache, setLocationsCache] = useState(new Map());
  const [loadingAccounts, setLoadingAccounts] = useState(new Set());
  const [openAccountId, setOpenAccountId] = useState(null);
  const [switchingAccountId, setSwitchingAccountId] = useState(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const [showPageLoader, setShowPageLoader] = useState(false);
  const closeTimerRef = useRef(null);
  const containerRef = useRef(null);
  const accountItemRefs = useRef(new Map());
  const submenuRef = useRef(null);

  // Current state from storage - read fresh on each render
  const currentAccountId = sessionStorage.getItem("acc_id");
  const currentLocation = JSON.parse(sessionStorage.getItem("current_loc") || "null");
  const currentAccountName = sessionStorage.getItem("acc_name") || "Account";

  // Fetch organizations list (only for account switching mode)
  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ["organizations-list"],
    queryFn: getOrganizationList,
    enabled: showAccountSwitcher,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch locations directly for location-only mode
  const { data: directLocationsData = [], isLoading: isLoadingDirectLocations } = useQuery({
    queryKey: ["locations-list-direct", currentAccountId],
    queryFn: async () => {
      const accId = sessionStorage.getItem("acc_id");
      const response = await getLocationList(false, accId);
      return response?.locations || [];
    },
    enabled: !showAccountSwitcher && showLocationSwitcher,
    staleTime: 5 * 60 * 1000,
  });

  // Switch mutation
  const switchMutation = useMutation({
    mutationFn: (data) => switchAccountLocation(data),
    onSuccess: () => {
      queryClient.clear();
      window.location.href = "/dashboard";
    },
    onError: (error) => {
      setShowPageLoader(false);
      setSwitchingAccountId(null);
      setOpenAccountId(null);
      setIsPopoverOpen(false);

      const status = error?.response?.status;
      if (status === 403 || status === 401) {
        toast.error("You don't have access to this account or location.");
      } else {
        toast.error(error?.response?.data?.error || "Failed to switch. Please try again.");
      }
    },
  });

  // Load locations for an account (lazy loading for account switching mode)
  const loadLocationsForAccount = useCallback(async (accountId) => {
    const accountIdStr = String(accountId);

    if (locationsCache.has(accountIdStr)) {
      return locationsCache.get(accountIdStr);
    }

    if (loadingAccounts.has(accountIdStr)) {
      return null;
    }

    setLoadingAccounts((prev) => new Set([...prev, accountIdStr]));

    try {
      const data = await getLocationList(false, accountId);
      const locations = data?.locations || [];

      setLocationsCache((prev) => {
        const newCache = new Map(prev);
        newCache.set(accountIdStr, locations);
        return newCache;
      });

      return locations;
    } catch (error) {
      if (error?.response?.status !== 403 && error?.response?.status !== 401) {
        toast.error("Failed to load locations. Please try again.");
      }
      return null;
    } finally {
      setLoadingAccounts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountIdStr);
        return newSet;
      });
    }
  }, [locationsCache, loadingAccounts]);

  // Calculate submenu position relative to viewport (for fixed positioning)
  const calculateSubmenuPosition = useCallback((accountId) => {
    const element = accountItemRefs.current.get(accountId);
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    const submenuWidth = 180;
    const submenuMaxHeight = 420;
    const spacing = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Default: position to the RIGHT of the account item.
    let left = rect.right + spacing;
    let side = "right";

    // If submenu would overflow right edge, fall back to the LEFT side.
    if (left + submenuWidth > viewportWidth - 8) {
      left = rect.left - spacing - submenuWidth;
      side = "left";
    }

    // Final clamp in case the trigger sits in a tight viewport.
    if (left < 8) left = 8;
    if (left + submenuWidth > viewportWidth - 8) {
      left = viewportWidth - submenuWidth - 8;
    }

    // Calculate top position (aligned with account item, but ensure it fits)
    let top = rect.top;

    // Check if submenu would go below viewport
    if (top + submenuMaxHeight > viewportHeight - 8) {
      // Try to align to bottom
      const alignBottom = rect.bottom - submenuMaxHeight;
      if (alignBottom >= 8) {
        top = alignBottom;
      } else {
        // Not enough space anywhere, use best fit
        top = Math.max(8, viewportHeight - submenuMaxHeight - 8);
      }
    }

    // Ensure top doesn't go off top edge
    if (top < 8) top = 8;

    return { top, left, side };
  }, []);

  // Update submenu position
  const updateSubmenuPosition = useCallback((accountId) => {
    const position = calculateSubmenuPosition(accountId);
    if (position) {
      setSubmenuPosition(position);
    }
  }, [calculateSubmenuPosition]);

  // Recalculate submenu position on scroll/resize when submenu is open
  useEffect(() => {
    if (!openAccountId) return;

    const handleUpdate = () => {
      updateSubmenuPosition(openAccountId);
    };

    // Use requestAnimationFrame for smoother updates
    let rafId = null;
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(handleUpdate);
    };

    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(handleUpdate);
    };

    // Listen to scroll and resize events
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [openAccountId, updateSubmenuPosition]);

  // Close the main popover (and its submenu) on a click that lands outside
  // the trigger, the popover content, and the locations submenu portal.
  useEffect(() => {
    if (!isPopoverOpen) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        containerRef.current?.contains(target) ||
        target.closest?.('[class*="accountsMenu"]') ||
        submenuRef.current?.contains(target)
      ) return;
      setIsPopoverOpen(false);
      setOpenAccountId(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsPopoverOpen(false);
        setOpenAccountId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPopoverOpen]);

  // Handle account switch - Matched to old switchModal implementation
  const handleAccountSwitch = useCallback(async (account) => {
    const currentAccId = sessionStorage.getItem("acc_id");
    if (String(currentAccId) === String(account?.id)) {
      toast.error("Account already active");
      return;
    }

    setSwitchingAccountId(String(account?.id));

    const prevUser = localStorage.getItem("user");
    const prevAccId = sessionStorage.getItem("acc_id");
    const prevAccName = sessionStorage.getItem("acc_name");
    const prevSchema = sessionStorage.getItem("schema_name");
    const prevCurrentLoc = sessionStorage.getItem("current_loc");
    const prevFallbackLocation = localStorage.getItem("fallback_location");
    const prevFallbackOrg = localStorage.getItem("fallback_org");

    const rollback = () => {
      if (prevUser) localStorage.setItem("user", prevUser);
      if (prevAccId) sessionStorage.setItem("acc_id", prevAccId);
      if (prevAccName) sessionStorage.setItem("acc_name", prevAccName);
      if (prevSchema) sessionStorage.setItem("schema_name", prevSchema);
      if (prevCurrentLoc) sessionStorage.setItem("current_loc", prevCurrentLoc);
      if (prevFallbackLocation) localStorage.setItem("fallback_location", prevFallbackLocation);
      if (prevFallbackOrg) localStorage.setItem("fallback_org", prevFallbackOrg);
    };

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) {
        user.current_account = account?.id;
        user.current_location = null;
        user.current_schema = account?.schema_name;
        localStorage.setItem("user", JSON.stringify(user));
      }

      sessionStorage.setItem("acc_id", account?.id);
      sessionStorage.setItem("acc_name", account?.name || "");
      sessionStorage.setItem("schema_name", account?.schema_name);
      sessionStorage.removeItem("current_loc");
      localStorage.removeItem("fallback_location");
      localStorage.setItem("fallback_org", JSON.stringify(account));

      let location_id;
      let locationsErrorStatus;
      try {
        await getLocationList(true);
        const cur = JSON.parse(sessionStorage.getItem("current_loc") || "null");
        location_id = cur?.id ? String(cur.id) : undefined;
      } catch (error) {
        locationsErrorStatus = error?.response?.status;
      }

      if (locationsErrorStatus === 403 || locationsErrorStatus === 401) {
        rollback();
        toast.error("You don't have access to this account.");
        setSwitchingAccountId(null);
        return;
      }

      if (locationsErrorStatus) {
        rollback();
        toast.error("Couldn't load locations for this account. Please try again.");
        setSwitchingAccountId(null);
        return;
      }

      if (!location_id) {
        rollback();
        toast.error("This account has no locations configured.");
        setSwitchingAccountId(null);
        return;
      }

      const data = {
        account_id: account?.id,
        location_id,
      };

      await switchMutation.mutateAsync(data);
    } catch (error) {
      rollback();
      const status = error?.response?.status;
      if (status === 403 || status === 401) {
        toast.error("You don't have access to this account.");
      } else {
        toast.error(error?.response?.data?.error || "Failed to switch account. Please try again.");
      }
      setSwitchingAccountId(null);
    }
  }, [switchMutation]);

  // Handle location click - Works for both modes
  const handleLocationClick = useCallback(async (location) => {
    const currentAccId = sessionStorage.getItem("acc_id");
    const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");
    const isSameLocation = currentLoc?.id == location?.id;

    if (isSameLocation) {
      toast.error("Location already active");
      return;
    }

    setShowPageLoader(true);
    setSwitchingAccountId(currentAccId);

    const prevUser = localStorage.getItem("user");
    const prevAccId = sessionStorage.getItem("acc_id");
    const prevAccName = sessionStorage.getItem("acc_name");
    const prevSchema = sessionStorage.getItem("schema_name");
    const prevCurrentLoc = sessionStorage.getItem("current_loc");
    const prevFallbackLocation = localStorage.getItem("fallback_location");
    const prevFallbackOrg = localStorage.getItem("fallback_org");

    const rollback = () => {
      if (prevUser) localStorage.setItem("user", prevUser);
      if (prevAccId) sessionStorage.setItem("acc_id", prevAccId);
      if (prevAccName) sessionStorage.setItem("acc_name", prevAccName);
      if (prevSchema) sessionStorage.setItem("schema_name", prevSchema);
      if (prevCurrentLoc) sessionStorage.setItem("current_loc", prevCurrentLoc);
      if (prevFallbackLocation) localStorage.setItem("fallback_location", prevFallbackLocation);
      if (prevFallbackOrg) localStorage.setItem("fallback_org", prevFallbackOrg);
    };

    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        user.current_location = location?.id;
        localStorage.setItem("user", JSON.stringify(user));
      }

      localStorage.setItem("fallback_location", JSON.stringify(location));
      sessionStorage.setItem("current_loc", JSON.stringify(location));

      const data = {
        account_id: currentAccId,
        location_id: String(location?.id),
      };

      await switchMutation.mutateAsync(data);
    } catch (error) {
      setShowPageLoader(false);
      rollback();
      const status = error?.response?.status;
      if (status === 403 || status === 401) {
        toast.error("You don't have access to this location.");
      } else {
        toast.error(error?.response?.data?.error || "Failed to switch. Please try again.");
      }
      setSwitchingAccountId(null);
    }
  }, [switchMutation]);

  // Handle location click with account context (for cross-account switching via submenu)
  const handleLocationClickWithAccount = useCallback(async (account, location) => {
    const currentAccId = sessionStorage.getItem("acc_id");
    const isSameAccount = String(currentAccId) === String(account?.id);

    if (isSameAccount) {
      // Same account: delegate to the simple location switch
      handleLocationClick(location);
      return;
    }

    // Cross-account: update storage for the new account first
    setShowPageLoader(true);
    setSwitchingAccountId(String(account?.id));

    const prevUser = localStorage.getItem("user");
    const prevAccId = sessionStorage.getItem("acc_id");
    const prevAccName = sessionStorage.getItem("acc_name");
    const prevSchema = sessionStorage.getItem("schema_name");
    const prevCurrentLoc = sessionStorage.getItem("current_loc");
    const prevFallbackLocation = localStorage.getItem("fallback_location");
    const prevFallbackOrg = localStorage.getItem("fallback_org");

    const rollback = () => {
      if (prevUser) localStorage.setItem("user", prevUser);
      if (prevAccId) sessionStorage.setItem("acc_id", prevAccId);
      if (prevAccName) sessionStorage.setItem("acc_name", prevAccName);
      if (prevSchema) sessionStorage.setItem("schema_name", prevSchema);
      if (prevCurrentLoc) sessionStorage.setItem("current_loc", prevCurrentLoc);
      if (prevFallbackLocation) localStorage.setItem("fallback_location", prevFallbackLocation);
      if (prevFallbackOrg) localStorage.setItem("fallback_org", prevFallbackOrg);
    };

    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        user.current_account = account?.id;
        user.current_location = location?.id;
        user.current_schema = account?.schema_name;
        localStorage.setItem("user", JSON.stringify(user));
      }

      sessionStorage.setItem("acc_id", account?.id);
      sessionStorage.setItem("acc_name", account?.name || "");
      sessionStorage.setItem("schema_name", account?.schema_name);
      sessionStorage.setItem("current_loc", JSON.stringify(location));
      localStorage.removeItem("fallback_location");
      localStorage.setItem("fallback_org", JSON.stringify(account));

      await switchMutation.mutateAsync({
        account_id: account?.id,
        location_id: String(location?.id),
      });
    } catch (error) {
      setShowPageLoader(false);
      rollback();
      const status = error?.response?.status;
      if (status === 403 || status === 401) {
        toast.error("You don't have access to this account or location.");
      } else {
        toast.error(error?.response?.data?.error || "Failed to switch. Please try again.");
      }
      setSwitchingAccountId(null);
    }
  }, [switchMutation, handleLocationClick]);

  // Handle account item click - auto-switches if exactly 1 selectable location,
  // opens the submenu if there are 0 or 2+.
  //
  // IMPORTANT: this reads the return value of loadLocationsForAccount()
  // directly rather than locationsCache, because setLocationsCache() is an
  // async state update — by the time the await above resolves, the
  // locationsCache captured in this closure is still the PREVIOUS render's
  // Map and won't contain the entry we just fetched. Using the resolved
  // value avoids that stale-read race on the very first click for any
  // not-yet-cached account.
  const handleAccountClick = useCallback(async (account, event) => {
    event.stopPropagation();
    event.preventDefault();

    // A switch is already in flight - ignore clicks until it resolves
    if (switchingAccountId) return;

    const accountId = String(account?.id);
    const selectableCount = account?.selectable_locations_count ?? 0;

    if (openAccountId === accountId) {
      setOpenAccountId(null);
      return;
    }

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    // Case: Exactly 1 selectable location - auto-switch silently without opening submenu
    if (selectableCount === 1) {
      // Mark the switch as in-flight BEFORE the await so a rapid second click
      // can't re-enter (the fetch would dedupe to null and fall back to the
      // submenu mid-switch). Also close any other account's submenu that was
      // left open, since this path never opens one of its own.
      setSwitchingAccountId(accountId);
      setOpenAccountId(null);

      const locations = await loadLocationsForAccount(accountId);

      if (locations && locations.length === 1) {
        const currentAccId = sessionStorage.getItem("acc_id");
        const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");
        if (
          String(currentAccId) === accountId &&
          String(currentLoc?.id) === String(locations[0]?.id)
        ) {
          // Already on this account's only location - close quietly instead of
          // surfacing the "Location already active" error toast
          setSwitchingAccountId(null);
          setIsPopoverOpen(false);
          return;
        }
        handleLocationClickWithAccount(account, locations[0]);
        return;
      }
      // Fallback: If locations didn't load correctly, show the submenu
      setSwitchingAccountId(null);
    }

    // Case: 0 or 2+ locations - open submenu for user selection
    setOpenAccountId(accountId);

    // Calculate position after state update, using requestAnimationFrame
    // to ensure the DOM has updated
    requestAnimationFrame(() => {
      updateSubmenuPosition(accountId);
    });

    await loadLocationsForAccount(accountId);
  }, [openAccountId, switchingAccountId, loadLocationsForAccount, updateSubmenuPosition, handleLocationClickWithAccount]);

  // Handle popover state change - close submenu when popover closes
  const handlePopoverChange = useCallback((open) => {
    setIsPopoverOpen(open);
    if (!open) {
      setOpenAccountId(null);
    }
  }, []);

  // Handle submenu mouse events
  const handleSubmenuEnter = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleSubmenuLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setOpenAccountId(null);
    }, 150);
  }, []);

  // Close submenu when clicking outside
  useEffect(() => {
    if (isPopoverOpen) return; // When main popover is open, it handles outside clicks

    const handleClickOutside = (event) => {
      const clickedInContainer = containerRef.current?.contains(event.target);
      const clickedInSubmenu = submenuRef.current?.contains(event.target);

      if (!clickedInContainer && !clickedInSubmenu) {
        setOpenAccountId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [isPopoverOpen]);

  // Prevent body scroll when dropdowns are open
  useEffect(() => {
    // Don't prevent scroll - it causes issues with submenu positioning
    // The PopoverContent handles its own scroll prevention if needed via disableScroll prop
    return () => {
      // Cleanup
    };
  }, []);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Sort organizations: current first, then alphabetically
  const sortedOrganizations = useMemo(() => {
    const currentAccId = sessionStorage.getItem("acc_id");
    return [...organizations].sort((a, b) => {
      const aIsCurrent = String(a?.id) === String(currentAccId);
      const bIsCurrent = String(b?.id) === String(currentAccId);
      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
      return (a?.name || "").localeCompare(b?.name || "");
    });
  }, [organizations]);

  // Sort locations: current first, then alphabetically
  const sortedDirectLocations = useMemo(() => {
    const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");
    if (!directLocationsData || !Array.isArray(directLocationsData)) return [];

    return [...directLocationsData].sort((a, b) => {
      const aIsCurrent = a?.id == currentLoc?.id;
      const bIsCurrent = b?.id == currentLoc?.id;
      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
      return (a?.name || "").localeCompare(b?.name || "");
    });
  }, [directLocationsData]);

  // Read fresh values for display
  const currentAccName = sessionStorage.getItem("acc_name") || "Account";
  const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");

  const displayName = showAccountSwitcher
    ? currentAccName
    : currentLoc?.name || "Select Location";
  const currentLocName = currentLoc?.name || "All Locations";

  const openAccountData = openAccountId ? organizations?.find(o => String(o?.id) === openAccountId) : null;
  const openLocations = openAccountId ? locationsCache.get(openAccountId) : null;
  const isLoadingLocations = openAccountId ? loadingAccounts.has(openAccountId) : false;

  // Handle location item click
  const handleLocationItemClick = (location) => {
    if (switchingAccountId) {
      return;
    }
    handleLocationClick(location);
  };

  return (
    <div className={styles.switchAccount} ref={containerRef}>
      <p className={styles.switchLabel}>
        {showAccountSwitcher ? "Switch Account / Location" : "Switch Location:"}
      </p>

      {/* MODE 1: Account Switching (TOCA users) */}
      {showAccountSwitcher && (
        <Popover open={isPopoverOpen} onOpenChange={handlePopoverChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Switch account, current: ${currentAccName} / ${currentLocName}`}
              className={`${styles.triggerButton} ${isPopoverOpen ? styles.triggerButtonOpen : ""}`}
            >
              <span className={styles.triggerTwoLine}>
                <span className={styles.triggerAccount} title={currentAccName}>
                  {currentAccName}
                </span>
                <span className={styles.triggerDivider} aria-hidden="true" />
                <span className={styles.triggerLocation} title={currentLocName}>
                  {currentLocName}
                </span>
              </span>
              <FaChevronDown size={12} className={`${styles.chevron} ${isPopoverOpen ? styles.chevronRotated : ""}`} />
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="end"
            sideOffset={8}
             zIndexOffset={1000000}
            className={styles.accountsMenu}
            closeOnClickOutside={false}
            disableScroll={true}
          >
            {isLoadingOrgs ? (
              <div className={styles.loadingOverlay}>
                <Spinner scale={4} />
              </div>
            ) : sortedOrganizations.length === 0 ? (
              <div className={styles.noResults}>No accounts available</div>
            ) : (
              <div className={styles.accountsList}>
                {sortedOrganizations.map((account) => {
                  const accountId = String(account?.id);
                  const currentAccId = sessionStorage.getItem("acc_id");
                  const isCurrent = accountId === String(currentAccId);
                  const isOpen = openAccountId === accountId;
                  const isLoading = loadingAccounts.has(accountId);
                  const isAccountSwitching = switchingAccountId === accountId;
                  const selectableCount = account?.selectable_locations_count ?? 0;
                  // Label falls back to locations_count on envs where the backend
                  // doesn't serve selectable_locations_count yet (behavior stays
                  // gated on the selectable count only)
                  const displayCount =
                    account?.selectable_locations_count ?? account?.locations_count ?? 0;

                  return (
                    <div
                      key={accountId}
                      ref={(el) => {
                        if (el) accountItemRefs.current.set(accountId, el);
                      }}
                      className={`${styles.accountItem} ${isOpen ? styles.accountItemOpen : ""} ${isCurrent ? styles.accountItemCurrent : ""}`}
                      onClick={(e) => !isAccountSwitching && handleAccountClick(account, e)}
                    >
                      <div className={styles.accountMain}>
                        <div className={styles.accountInfo}>
                          <span className={styles.accountName}>
                            {account?.name}
                            {isCurrent && (
                              <span
                                className={styles.activeBadge}
                                aria-label="Active"
                                title="Active"
                              >
                                <FaCheck size={8} />
                              </span>
                            )}
                          </span>
                          <span className={styles.locationCount}>
                            {displayCount} location{displayCount !== 1 ? 's' : ''}
                          </span>
                        </div>

                        <div className={styles.accountActions}>
                          {isAccountSwitching ? (
                            <Spinner scale={1.5} color="#10164d" />
                          ) : isLoading ? (
                            <Spinner scale={1.5} color="#9ca3af" />
                          ) : selectableCount === 1 ? null : (
                            // No chevron for single-location accounts - clicking
                            // switches directly, there is no submenu to expand
                            <div className={styles.chevronWrapper}>
                              <FaChevronRight size={12} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PopoverContent>
        </Popover>

      )}

      {/* MODE 2: Location-Only (non-TOCA users) */}
      {!showAccountSwitcher && showLocationSwitcher && (
        <Popover open={isPopoverOpen} onOpenChange={handlePopoverChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Switch location, current: ${displayName}`}
              className={`${styles.triggerButton} ${isPopoverOpen ? styles.triggerButtonOpen : ""}`}
            >
              <span className={styles.triggerText}>{displayName}</span>
              <FaChevronDown size={12} className={`${styles.chevron} ${isPopoverOpen ? styles.chevronRotated : ""}`} />
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="end"
            sideOffset={8}
            className={styles.accountsMenu}
            zIndexOffset={1000000}
            closeOnClickOutside={false}
            disableScroll={true}
          >
            {isLoadingDirectLocations ? (
              <div className={styles.loadingOverlay}>
                <Spinner scale={4} />
              </div>
            ) : sortedDirectLocations.length === 0 ? (
              <div className={styles.noResults}>No locations available</div>
            ) : (
              <div className={styles.locationsList}>
                {sortedDirectLocations.map((location, index) => {
                  const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");
                  // Match by location name for more reliable active state
                  const isCurrent = location?.name === currentLoc?.name;
                  const isLocationSwitching = showPageLoader && !isCurrent;

                  return (
                    <div
                      key={location?.id}
                      className={`${styles.locationItem} ${isCurrent ? styles.locationItemCurrent : ""} ${isLocationSwitching ? styles.locationItemDisabled : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLocationItemClick(location);
                      }}
                      style={{ animationDelay: `${index * 20}ms` }}
                    >
                      <div className={styles.locationMain}>
                        <span className={styles.locationName}>
                          {location?.name}
                          {isCurrent && (
                            <span
                              className={styles.activeBadge}
                              aria-label="Current"
                              title="Current"
                            >
                              <FaCheck size={8} />
                            </span>
                          )}
                        </span>
                        {isLocationSwitching && (
                          <div className={styles.locationStatus}>
                            <Spinner scale={1.2} color="#10164d" />
                          </div>
                        )}
                      </div>
                      {location?.active_patients != null && (
                        <div className={styles.locationStats}>
                          {location.active_patients} Active{" "}
                          {location.active_patients === 1 ? "Patient" : "Patients"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}

      {/* Locations Submenu via portal (only for account switching mode) */}
      {showAccountSwitcher && openAccountId && openAccountData && submenuPosition.top !== 0 && (
        <LocationsSubmenu
          ref={submenuRef}
          account={openAccountData}
          locations={openLocations}
          isLoadingLocations={isLoadingLocations}
          isSwitching={switchingAccountId === openAccountId}
          currentLocation={currentLocation}
          position={submenuPosition}
          onLocationClick={(location) => handleLocationClickWithAccount(openAccountData, location)}
          onMouseEnter={handleSubmenuEnter}
          onMouseLeave={handleSubmenuLeave}
        />
      )}

      {/* Full-page loader during switch */}
      {showPageLoader && createPortal(
        <div className={styles.pageLoaderOverlay}>
          <div className={styles.pageLoaderContent}>
            <Spinner scale={8} color="#10164d" />
            <p className={styles.pageLoaderText}>Switching...</p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Locations Submenu Component (portal-rendered, only for account switching mode)
const LocationsSubmenu = React.memo(React.forwardRef(({
  account,
  locations,
  isLoadingLocations,
  isSwitching,
  currentLocation,
  position,
  onLocationClick,
  onMouseEnter,
  onMouseLeave,
}, ref) => {
  const internalSubmenuRef = useRef(null);

  const setSubmenuRef = (node) => {
    internalSubmenuRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const sortedLocations = useMemo(() => {
    if (!locations) return [];
    const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");

    const sorted = [...locations].sort((a, b) => {
      const aIsCurrent = a?.id == currentLoc?.id;
      const bIsCurrent = b?.id == currentLoc?.id;
      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
      return (a?.name || "").localeCompare(b?.name || "");
    });
    return sorted;
  }, [locations]);

  const handleLocationItemClick = (location) => {
    if (isSwitching) {
      return;
    }
    onLocationClick(location);
  };

  // Don't render if position is invalid
  if (!position || position.top === 0 && position.left === 0) {
    return null;
  }

  const submenuContent = (
    <div
      ref={setSubmenuRef}
      className={`${styles.locationsSubmenu} ${position.side === "left" ? styles.locationsSubmenuLeft : ""}`}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: "180px",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.submenuHeader}>
        <span className={styles.submenuTitle}>{account?.name}</span>
      </div>

      {isLoadingLocations ? (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <Spinner scale={3} color="#10164d" />
            <span className={styles.loadingText}>Loading locations...</span>
          </div>
        </div>
      ) : !sortedLocations || sortedLocations.length === 0 ? (
        <div className={styles.noResults}>No locations available</div>
      ) : (
        <div className={styles.locationsList}>
          {sortedLocations.map((location, index) => {
            const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");
            // Match by location name for more reliable active state
            const isCurrent = location?.name === currentLoc?.name;
            const isLocationSwitching = isSwitching && !isCurrent;

            return (
              <div
                key={location?.id}
                className={`${styles.locationItem} ${isCurrent ? styles.locationItemCurrent : ""} ${isLocationSwitching ? styles.locationItemDisabled : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLocationItemClick(location);
                }}
                style={{ animationDelay: `${index * 20}ms` }}
              >
                <div className={styles.locationMain}>
                  <span className={styles.locationName}>
                    {location?.name}
                    {isCurrent && (
                      <span
                        className={styles.activeBadge}
                        aria-label="Current"
                        title="Current"
                      >
                        <FaCheck size={8} />
                      </span>
                    )}
                  </span>
                  {isLocationSwitching && (
                    <div className={styles.locationStatus}>
                      <Spinner scale={1.2} color="#10164d" />
                    </div>
                  )}
                </div>
                {location?.active_patients != null && (
                  <div className={styles.locationStats}>
                    {location.active_patients} Active{" "}
                    {location.active_patients === 1 ? "Patient" : "Patients"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return createPortal(submenuContent, document.body);
}));

LocationsSubmenu.displayName = "LocationsSubmenu";

export default AccountLocationSwitcher;
